<template>
  <div>
    <load-profile></load-profile>
    <!-- Search Container Card -->
    <n-search-container
      :active="true"
      :show-header="false"
      @search="search"
      show-search
      @reset="reset"
      v-if="searchFields.length"
    >
      <n-search-input ref="search" :fields="searchFields" v-model="params">
        <template #typeId="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              :ref="item.field.key"
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                :value="option.value"
                v-for="option in item.field.options"
                :key="option.value"
                >{{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>

        <template #period="item">
          <b-form-group
            :label-for="item.field.key"
            :label="$t(item.field.label)"
          >
            <b-form-radio-group
              :ref="item.field.key"
              v-if="item.field.type === 'radio'"
              :id="item.field.key"
              v-model="params[item.field.key]"
              buttons
              button-variant="outline-primary rounded-0"
              class="flex-wrap"
            >
              <b-form-radio
                :value="option.value"
                v-for="option in item.field.options"
                :key="option.value"
              >
                {{ $t(option.text) }}
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </template>
      </n-search-input>
    </n-search-container>

    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-0-5">
        <b-row class="mb-2" v-if="$store.state.profile.id !== user.id">
          <b-col cols="6">
            <b-button type="button" variant="outline-secondary" @click="back">
              <feather-icon icon="ChevronLeftIcon" />
              {{ $t("button.back") }}
            </b-button>
          </b-col>
          <b-col cols="6" class="text-right">
            <b-button
              type="button"
              variant="outline-secondary"
              @click="getData"
            >
              <feather-icon icon="RefreshCcwIcon" />
              {{ $t("button.refresh") }}
            </b-button>
          </b-col>
        </b-row>
        <b-row class="mx-2 my-1" v-else>
          <b-col cols="12" class="text-right">
            <b-button
              type="button"
              variant="outline-secondary"
              @click="getData"
            >
              <feather-icon icon="RefreshCcwIcon" />
              {{ $t("button.refresh") }}
            </b-button>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          @sort-changed="sortChanged"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
        >
          <template #thead-top="" v-if="$store.state.profile.id !== user.id">
            <b-tr class="text-center">
              <b-th :colspan="typeId !== 5 && user.typeId !== 5 ? 15 : 13">
                {{ user.name }}
              </b-th>
            </b-tr>
          </template>
          <template #cell(username)="data">
            <b-link
              class="link-underline"
              @click="viewMember(data.item.userId)"
              v-if="typeId !== 5 && user.typeId !== 5"
            >
              {{ data.item.username }}
            </b-link>
            <span v-else>
              {{ data.item.username }}
            </span>
          </template>
          <template #cell(turnover)="data">
            <span class="text-positive">
              {{ data.item.turnover | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(commission)="data">
            <span class="text-positive">
              {{ data.item.commission | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(newWl)="data">
            <b-link
              @click="openBetHistory(data.item)"
              class="link-underline"
              :class="{
                'text-positive': data.item.newWl >= 0,
                'text-danger': data.item.newWl < 0,
              }"
            >
              {{ data.item.newWl | currency(data.item.ccy) }}
            </b-link>
          </template>
          <template #cell(deposit)="data">
            <span
              :class="{
                'text-positive': data.item.deposit >= 0,
              }"
            >
              {{ data.item.deposit | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(withdrawal)="data">
            <span
              :class="{
                'text-danger': data.item.withdrawal >= 0,
              }"
            >
              {{ data.item.withdrawal | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(balance)="data">
            <span
              :class="{
                'text-positive': data.item.balance >= 0,
                'text-danger': data.item.balance < 0,
              }"
            >
              {{ data.item.balance | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(outstanding)="data">
            <span
              :class="{
                'text-positive': data.item.outstanding >= 0,
                'text-danger': data.item.outstanding < 0,
              }"
            >
              {{ data.item.outstanding | currency(data.item.ccy) }}
            </span>
          </template>
          <template #cell(member)="data">
            <b-link
              class="link-underline"
              @click="viewMember(data.item.userId)"
              v-if="typeId !== 5 && user.typeId !== 5"
            >
              {{ data.item.member }}
            </b-link>
            <span v-else>
              {{ data.item.member }}
            </span>
          </template>
          <template #cell(status)="data">
            <b-badge pill variant="success" badge-glow v-if="data.item.status">
              {{ $t(`status.active`) }}
            </b-badge>
            <b-badge pill variant="danger" badge-glow v-else>
              {{ $t(`status.inactive`) }}
            </b-badge>
          </template>
          <template #cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                v-if="$can('create', 'agent') || $can('edit', 'agent')"
                v-b-tooltip.hover
                @click="
                  showDepositOrWithdrawModel(
                    data.item.userId,
                    data.item.username,
                    data.item.balance,
                    data.item.ccy
                  )
                "
                :title="$t('button.deposit')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="CreditCardIcon" />
              </b-button>
              <b-button
                v-if="$can('create', 'agent') || $can('edit', 'agent')"
                v-b-tooltip.hover
                @click="
                  showDepositOrWithdrawModel(
                    data.item.userId,
                    data.item.username,
                    data.item.balance,
                    data.item.ccy,
                    'withdrawal'
                  )
                "
                :title="$t('button.withdraw')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="RotateCcwIcon" />
              </b-button>
            </div>
          </template>
          <template #custom-foot>
            <b-tr v-if="!loading">
              <b-th colspan="3"></b-th>
              <b-th class="text-right">
                <span class="text-positive">
                  {{ totalReport.turnover | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <b-th class="text-right" v-if="typeId !== 5 && user.typeId !== 5">
                <span class="text-positive">
                  {{ totalReport.commission | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <b-th
                class="text-right"
                :class="{
                  'text-positive': totalReport.newWl >= 0,
                  'text-danger': totalReport.newWl < 0,
                }"
              >
                {{ totalReport.newWl | currency(totalReport.ccy) }}
              </b-th>
              <b-th class="text-right">
                <span
                  :class="{
                    'text-positive': totalReport.deposit >= 0,
                  }"
                >
                  {{ totalReport.deposit | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <b-th class="text-right">
                <span
                  :class="{
                    'text-danger': totalReport.withdrawal >= 0,
                  }"
                >
                  {{ totalReport.withdrawal | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <b-th class="text-right">
                <span
                  :class="{
                    'text-positive': totalReport.balance >= 0,
                    'text-danger': totalReport.balance < 0,
                  }"
                >
                  {{ totalReport.balance | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <!-- Agent no need to see member column of players list -->
              <b-th
                class="text-center"
                v-if="typeId !== 5 && user.typeId !== 5"
              >
                {{ totalReport.member }}
              </b-th>
              <!-- Agent should see outstanding column of players list -->
              <b-th class="text-center">
                <span
                  :class="{
                    'text-positive': totalReport.outstanding >= 0,
                    'text-danger': totalReport.outstanding < 0,
                  }"
                >
                  {{ totalReport.outstanding | currency(totalReport.ccy) }}
                </span>
              </b-th>
              <b-th colspan="1"></b-th>
              <b-th colspan="1"></b-th>
            </b-tr>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="params.page"
          @change="list"
        ></n-pagination>
      </div>
    </b-card>
    <deposit-or-withdraw
      ref="depositWithdrawModel"
      @success="depositOrWithdrawSuccess"
    ></deposit-or-withdraw>
    <bet-history ref="betHistoryModal"></bet-history>
  </div>
</template>

<script>
import {
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
  BInputGroup,
  BLink,
  BRow,
  BTh,
  BTr,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Repository from "@/repositories/RepositoryFactory";
import NPagination from "@/components/NPagination";
import NSearchContainer from "@/components/NSearchContainer";
import NSearchInput from "@/components/NSearchInput";
import NButtonDelete from "@/components/NButtonDelete";
import NTable from "@/components/NTable";
import tableFields from "./tableField";
import playerTableFields from "./playerTableField";
import searchInputs from "./searchInput";
import { avatarText } from "@core/utils/filter";
import { calculateDatesByPeriod } from "@/libs/helper.js";
import DepositOrWithdraw from "@/views/account/depositOrWithdraw";
import moment from "moment";
import BetHistory from "./BetHistory/List";

const BalanceReportRepository = Repository.get("balanceReport");

export default {
  components: {
    BLink,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    NPagination,
    NSearchContainer,
    NSearchInput,
    NButtonDelete,
    NTable,
    vSelect,
    BFormGroup,
    BInputGroup,
    BBadge,
    BFormRadioGroup,
    BFormRadio,
    BTr,
    BTh,
    DepositOrWithdraw,
    BetHistory,
  },
  watch: {
    perPage(value) {
      this.list(1);
    },
    "params.period": function (value) {
      const dates = calculateDatesByPeriod(value);
      if (dates.length) {
        this.params.startDate = dates[0];
        this.params.toDate = dates[1];
      }
    },
    "user.typeId": function (value) {
      if (value) {
        this.changeCol(value);
      }
    },
    typeId(value) {
      this.setSearchFields(value);
      this.changeCol(value);
    },
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage;
      },
      set(value) {
        this.$store.commit("pagination/SET_PER_PAGE", value);
      },
    },
    activeSearch() {
      return (
        !!this.$route.query.userId ||
        !!this.$route.query.startDate ||
        !!this.$route.query.toDate
      );
    },
    typeId() {
      if (this.$store.state.profile.typeId) {
        return this.$store.state.profile.typeId;
      }
      return this.userData?.typeId;
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        userId: Number(this.$route.query.userId) || "",
        typeId: Number(this.$route.query.typeId) || "",
        startDate: this.$route.query.startDate || moment().format("YYYY-MM-DD"),
        toDate: this.$route.query.toDate || moment().format("YYYY-MM-DD"),
        period: this.$route.query.period || "today",
        search: this.$route.query.search || "",
      },
      totalReport: {},
      avatarText,
      user: this.$store.state.profile,
      searchFields: [],
    };
  },
  mounted() {
    this.getData(this.params.page);
    if (this.$store.state.profile.typeId) {
      this.changeCol(this.$store.state.profile.typeId);
      this.setSearchFields(this.$store.state.profile.typeId);
    }
  },
  methods: {
    changeCol(value) {
      if (value === 5) {
        this.fields = [...playerTableFields];
      } else {
        this.fields = [...tableFields];
      }
    },
    setSearchFields(userTypeId) {
      let typeSearch = {
        key: "typeId",
        label: "field.type",
        type: "radio",
        cols: 6,
      };
      if (userTypeId === 1 || userTypeId === 8) {
        typeSearch["options"] = [
          {
            value: 2,
            text: "breadcrumb.company",
          },
          {
            value: 3,
            text: "breadcrumb.senior",
          },
          {
            value: 4,
            text: "breadcrumb.master",
          },
          {
            value: 5,
            text: "breadcrumb.agent",
          },
          {
            value: 6,
            text: "breadcrumb.player",
          },
        ];
      } else if (userTypeId === 2) {
        typeSearch["options"] = [
          {
            value: 3,
            text: "breadcrumb.senior",
          },
          {
            value: 4,
            text: "breadcrumb.master",
          },
          {
            value: 5,
            text: "breadcrumb.agent",
          },
          {
            value: 6,
            text: "breadcrumb.player",
          },
        ];
      } else if (userTypeId === 3) {
        typeSearch["options"] = [
          {
            value: 4,
            text: "breadcrumb.master",
          },
          {
            value: 5,
            text: "breadcrumb.agent",
          },
          {
            value: 6,
            text: "breadcrumb.player",
          },
        ];
      } else if (userTypeId === 4) {
        typeSearch["options"] = [
          {
            value: 5,
            text: "breadcrumb.agent",
          },
          {
            value: 6,
            text: "breadcrumb.player",
          },
        ];
      } else if (userTypeId === 5) {
        typeSearch["options"] = [
          {
            value: 6,
            text: "breadcrumb.player",
          },
        ];
      }
      this.searchFields = [...searchInputs, typeSearch];
    },
    getUserTypeId() {
      if (this.$store.state.profile.typeId) {
        userTypeId = this.$store.state.profile.typeId;
      } else {
        userTypeId = null;
      }
    },
    back() {
      this.params.userId = this.user.parentId;
      this.params.page = 1;
      this.updateQuerySting();
      this.getData();
    },
    viewMember(userId) {
      this.params.userId = userId;
      this.params.page = 1;
      this.updateQuerySting();
      this.getData();
    },
    updateQuerySting() {
      this.$router.replace({ query: this.params });
    },
    list(page = 1) {
      this.params.page = page;
      this.updateQuerySting();
      this.getData();
    },
    sortChanged(sortBy, sortDirection) {
      this.params.order = sortBy;
      this.params.sort = sortDirection;
      this.updateQuerySting();
      this.getData();
    },
    search(searchText) {
      this.params.search = searchText;
      this.updateQuerySting();
      this.getData();
    },
    reset() {
      this.params.userId = null;
      this.$refs.search.reset();
      this.updateQuerySting();
      this.getData();
    },
    getData() {
      this.loading = true;
      BalanceReportRepository.index(this.params)
        .then((response) => {
          let data = response.data.data;
          this.items = [...data.list];
          this.total = data.total;
          this.user = {
            ...data.user,
          };
          this.totalReport = {};
          this.loading = false;
          this.getTotal();
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getTotal() {
      BalanceReportRepository.total({
        ...this.params,
      })
        .then((response) => {
          const data = response.data.data;
          this.totalReport = {
            ...data,
          };
        })
        .catch(() => {});
    },
    showDepositOrWithdrawModel(
      id,
      username,
      availableCredit,
      ccy,
      type = "deposit"
    ) {
      this.$refs.depositWithdrawModel.changeData({
        type: type,
        username: username,
        amount: 0,
        userId: id,
        remark: null,
        availableCredit: availableCredit,
        ccy: ccy,
      });

      this.$refs.depositWithdrawModel.show(
        `${this.$t("field.deposit")} (${username})`
      );
    },
    depositOrWithdrawSuccess() {
      this.getData();
    },
    openBetHistory(item) {
      const params = {
        page: 1,
        order: null,
        sort: null,
        userId: item.userId,
        startDate: this.params.startDate,
        toDate: this.params.toDate,
        period: this.params.period,
      };

      this.$refs.betHistoryModal.show(params);
    },
  },
  setup() {
    let fields = [...tableFields];
    return {
      fields,
    };
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
